import React from 'react';

import './index.scss';
import { useClassnames } from '@lumapps/classnames';

/**
 * Interface defining the properties for the TextList component.
 */
export interface TextListProps {
    /** The type of list to use ('ul' for unordered list, 'ol' for ordered list). */
    as: 'ul' | 'ol';
    /** The items to list. */
    children: React.ReactNode;
    /**
     * Controls the alignment of list markers (bullet points or numbers) relative to the text content.
     * - 'outside': Items are aligned by their content. Markers are in negative left margin.
     * - 'inside': Items are aligned by their marker.
     */
    listStylePosition?: 'inside' | 'outside';
    /** The classname to pass to the ListTag */
    className?: string;
    /** html id to forward if needed */
    id?: string;
    /** list style */
    listStyle?: React.CSSProperties['listStyle'];
}

const CLASSNAME = 'lumx-text-list';

/**
 * Component for displaying a basic list of items.
 * @family Texts
 */
export const TextList = ({ as, className, children, listStylePosition, id, listStyle = 'initial' }: TextListProps) => {
    const ListTag = ['ul', 'ol'].includes(as) ? as : 'ul';
    const { block } = useClassnames(CLASSNAME);

    return (
        <ListTag
            id={id}
            className={block(
                {
                    inside: listStylePosition === 'inside',
                    outside: listStylePosition === 'outside',
                },
                className,
            )}
            style={{ listStyle }}
        >
            {children}
        </ListTag>
    );
};

/**
 * Interface defining the properties for the TextListItem component.
 */
export interface TextListItemProps {
    /** The items to display in the list item. */
    children: React.ReactNode;
    /** custom classname */
    className?: string;
}

/**
 * Subcomponent for individual list items.
 * @family Texts
 */
TextList.Item = function Item({ children, className }: TextListItemProps) {
    const { element } = useClassnames(CLASSNAME);
    return <li className={element('item', [className])}>{children}</li>;
};
